import snakecaseKeys from 'snakecase-keys';
import { toChainFormat } from '@injectivelabs/utils';
import { GoogleProtobufAny, CosmosGovV1Beta1Tx, CosmosBaseV1Beta1Coin, InjectiveExchangeV1Beta1Proposal, InjectiveExchangeV1Beta1Exchange, } from '@injectivelabs/core-proto-ts';
import { MsgBase } from '../../MsgBase.js';
import { numberToCosmosSdkDecString } from '../../../../utils/numbers.js';
const createSpotMarketParamUpdate = (params) => {
    const content = InjectiveExchangeV1Beta1Proposal.SpotMarketParamUpdateProposal.create();
    content.title = params.market.title;
    content.description = params.market.description;
    content.marketId = params.market.marketId;
    content.makerFeeRate = params.market.makerFeeRate;
    content.takerFeeRate = params.market.takerFeeRate;
    content.relayerFeeShareRate = params.market.relayerFeeShareRate;
    content.minPriceTickSize = params.market.minPriceTickSize;
    content.minQuantityTickSize = params.market.minQuantityTickSize;
    content.status = params.market.status;
    content.ticker = params.market.ticker;
    content.baseDecimals = params.market.baseDecimals;
    content.quoteDecimals = params.market.quoteDecimals;
    content.minNotional = params.market.minNotional;
    if (params.market.adminInfo) {
        const adminInfo = InjectiveExchangeV1Beta1Proposal.AdminInfo.create();
        adminInfo.admin = params.market.adminInfo.admin;
        adminInfo.adminPermissions = params.market.adminInfo.adminPermissions;
        content.adminInfo = adminInfo;
    }
    return InjectiveExchangeV1Beta1Proposal.SpotMarketParamUpdateProposal.fromPartial(content);
};
/**
 * @category Messages
 */
export default class MsgSubmitProposalSpotMarketParamUpdate extends MsgBase {
    static fromJSON(params) {
        return new MsgSubmitProposalSpotMarketParamUpdate(params);
    }
    toProto() {
        const { params: initialParams } = this;
        const params = {
            ...initialParams,
            market: {
                ...initialParams.market,
                relayerFeeShareRate: toChainFormat(initialParams.market.relayerFeeShareRate).toFixed(),
                makerFeeRate: toChainFormat(initialParams.market.makerFeeRate).toFixed(),
                takerFeeRate: toChainFormat(initialParams.market.takerFeeRate).toFixed(),
                minPriceTickSize: toChainFormat(initialParams.market.minPriceTickSize).toFixed(),
                minNotional: toChainFormat(initialParams.market.minNotional).toFixed(),
                minQuantityTickSize: toChainFormat(initialParams.market.minQuantityTickSize).toFixed(),
            },
        };
        const depositParams = CosmosBaseV1Beta1Coin.Coin.create();
        depositParams.denom = params.deposit.denom;
        depositParams.amount = params.deposit.amount;
        const contentAny = GoogleProtobufAny.Any.create();
        contentAny.typeUrl =
            '/injective.exchange.v1beta1.SpotMarketParamUpdateProposal';
        contentAny.value =
            InjectiveExchangeV1Beta1Proposal.SpotMarketParamUpdateProposal.encode(createSpotMarketParamUpdate(params)).finish();
        const message = CosmosGovV1Beta1Tx.MsgSubmitProposal.create();
        message.content = contentAny;
        message.initialDeposit = [depositParams];
        message.proposer = params.proposer;
        return CosmosGovV1Beta1Tx.MsgSubmitProposal.fromPartial(message);
    }
    toData() {
        const proto = this.toProto();
        return {
            '@type': '/cosmos.gov.v1beta1.MsgSubmitProposal',
            ...proto,
        };
    }
    toAmino() {
        const { params } = this;
        const content = createSpotMarketParamUpdate(params);
        const messageWithProposalType = snakecaseKeys({
            content: {
                type: 'exchange/SpotMarketParamUpdateProposal',
                value: content,
            },
            initial_deposit: [
                {
                    denom: params.deposit.denom,
                    amount: params.deposit.amount,
                },
            ],
            proposer: params.proposer,
        });
        return {
            type: 'cosmos-sdk/MsgSubmitProposal',
            value: messageWithProposalType,
        };
    }
    toWeb3Gw() {
        const amino = this.toAmino();
        const { value } = amino;
        const messageWithProposalType = {
            ...value,
            content: {
                '@type': '/injective.exchange.v1beta1.SpotMarketParamUpdateProposal',
                ...value.content.value,
            },
        };
        return {
            '@type': '/cosmos.gov.v1beta1.MsgSubmitProposal',
            ...messageWithProposalType,
        };
    }
    toEip712() {
        const { params } = this;
        const amino = this.toAmino();
        const { value, type } = amino;
        const messageAdjusted = {
            ...value,
            content: {
                type: 'exchange/SpotMarketParamUpdateProposal',
                value: {
                    ...value.content.value,
                    relayer_fee_share_rate: toChainFormat(params.market.relayerFeeShareRate).toFixed(),
                    maker_fee_rate: toChainFormat(params.market.makerFeeRate).toFixed(),
                    taker_fee_rate: toChainFormat(params.market.takerFeeRate).toFixed(),
                    min_price_tick_size: toChainFormat(params.market.minPriceTickSize).toFixed(),
                    min_notional: toChainFormat(params.market.minNotional).toFixed(),
                    min_quantity_tick_size: toChainFormat(params.market.minQuantityTickSize).toFixed(),
                },
            },
        };
        return {
            type,
            value: messageAdjusted,
        };
    }
    toEip712V2() {
        const { params } = this;
        const web3gw = this.toWeb3Gw();
        const content = web3gw.content;
        const messageAdjusted = {
            ...web3gw,
            content: {
                ...content,
                status: InjectiveExchangeV1Beta1Exchange.marketStatusToJSON(content.status),
                relayer_fee_share_rate: numberToCosmosSdkDecString(params.market.relayerFeeShareRate),
                maker_fee_rate: numberToCosmosSdkDecString(params.market.makerFeeRate),
                taker_fee_rate: numberToCosmosSdkDecString(params.market.takerFeeRate),
                min_price_tick_size: numberToCosmosSdkDecString(params.market.minPriceTickSize),
                min_notional: numberToCosmosSdkDecString(params.market.minNotional),
                min_quantity_tick_size: numberToCosmosSdkDecString(params.market.minQuantityTickSize),
            },
        };
        return messageAdjusted;
    }
    toDirectSign() {
        const proto = this.toProto();
        return {
            type: '/cosmos.gov.v1beta1.MsgSubmitProposal',
            message: proto,
        };
    }
    toBinary() {
        return CosmosGovV1Beta1Tx.MsgSubmitProposal.encode(this.toProto()).finish();
    }
}
